import { tetriFromDecimal, tetriToDecimalString } from '@/utils/money';
import type {
    ChartAccount,
    OperationLineSide,
    OperationSlotChoice,
    RecordableOperation,
    RecordableOperations,
    RecordableSlot,
    RecordingPeriodStatus,
} from '@/api/models';
import { formatInLanguage } from '@/utils/dateLocale';
import type { ErrorResponse } from '@/api/handlers/responses';
import type { LocaleCode } from '@/api/models';
import {
    isoToDisplayDate,
    toIsoDate,
} from '@/pages/accounting/statements/statementMeta';

/**
 * The Record tab's pure helpers and presentational config.
 *
 * Everything here is a function of its arguments. The hook holds the form in
 * `useState` and moves it by calling these; the View renders what comes out.
 * That is what lets the fiddly parts — which slots are answered, which accounts
 * a filter admits, where a server refusal belongs on screen — be exercised
 * without rendering anything.
 */

/** How long the form must sit still before a preview is asked for. */
export const PREVIEW_DEBOUNCE_MS = 400;

/**
 * How the amount field draws itself: GEL, with the ₾ sign rather than the ISO
 * code.
 *
 * `narrowSymbol` matches the template editor's sample field and every figure the
 * impact panel prints beside it — the one editable amount on the screen should
 * not be the only one written differently from the way the ledger writes money.
 */
export const AMOUNT_FORMAT: Intl.NumberFormatOptions = {
    currency: 'GEL',
    currencyDisplay: 'narrowSymbol',
    style: 'currency',
};

/**
 * The seam between the amount field and the money contract.
 *
 * `RecordForm.amountGel` is a **string** all the way to `parseGelToTetri`, which
 * does integer arithmetic on its digits — that is the repo's rule that floats
 * never touch an amount. `NumberField` speaks `number`, so these two functions
 * are the only place the two representations meet, and they are written so the
 * float cannot carry anything into the ledger:
 *
 * **The shared money helpers, never `String()`.** `tetriFromDecimal` reads the
 * float by digit position and `tetriToDecimalString` prints the integer back, so
 * the string handed on can never describe a fraction of a tetri — and unlike
 * `String()` it does not produce exponent notation (`1e+21`) for an
 * absurd value, which `parseGelToTetri` would read as invalid rather than as the
 * number it is. Values large enough that `toFixed` gives up and returns
 * exponential are refused downstream, which is the right answer for an amount no
 * ledger will book.
 */
export const amountToGelString = (value: number | undefined): string =>
    value === undefined || Number.isNaN(value)
        ? ''
        : tetriToDecimalString(tetriFromDecimal(value));

/**
 * The other direction: the stored string as the field's value.
 *
 * **`NaN` is the empty value, not `undefined`**, and the difference is a bug
 * rather than a preference. `NumberField` reads `undefined` as "uncontrolled"
 * and keeps whatever text it last had, so clearing the form after a post left
 * `₾118.00` sitting in a field the form believed was empty — a figure the user
 * had not entered, on a screen whose next act is posting one. `NaN` is the
 * sentinel React Aria clears on. It never coerces to `0` either, which would be
 * the same bug wearing a plausible number.
 */
export const gelStringToAmount = (gel: string): number => {
    if (gel.trim() === '') {
        return Number.NaN;
    }
    const parsed = Number(gel);
    return Number.isFinite(parsed) ? parsed : Number.NaN;
};

/** The form, as the user fills it. */
export type RecordForm = {
    /**
     * ISO `YYYY-MM-DD`. Which template version applies is resolved from it,
     * which is why it is rendered **above** the picker as step 1 rather than
     * inside the generated form — see `components/dateBand`.
     */
    date: string;
    /** As typed, in GEL. `parseGelToTetri` is the only crossing into tetri. */
    amountGel: string;
    /** Account id per slot line id. A slot the user has not answered is absent. */
    slots: Record<string, string>;
    reference: string;
    note: string;
};

export const emptyForm = (date: string): RecordForm => ({
    date,
    amountGel: '',
    slots: {},
    reference: '',
    note: '',
});

/**
 * The form for a newly-picked operation, keeping the fields that survive the
 * change.
 *
 * The date, the reference and the note carry over; the slots do not, because
 * they are answers to *that* operation's questions and a line id from another
 * template means nothing here. The amount carries over too — someone recording
 * three ₾40 expenses in a row is the case this screen is for.
 */
export const formForOperation = (
    current: RecordForm,
    operation: RecordableOperation,
): RecordForm => ({
    ...current,
    slots: Object.fromEntries(
        operation.slots
            .filter((slot) => current.slots[slot.lineId])
            .map((slot) => [slot.lineId, current.slots[slot.lineId] as string]),
    ),
});

/** Every slot answered with a non-empty account. */
export const isComplete = (
    form: RecordForm,
    operation: RecordableOperation | null,
): boolean =>
    operation !== null &&
    operation.slots.every((slot) => Boolean(form.slots[slot.lineId]));

/** The form's slot answers, in the shape the request takes. */
export const toSlotChoices = (
    form: RecordForm,
    operation: RecordableOperation | null,
): OperationSlotChoice[] =>
    (operation?.slots ?? [])
        .map((slot) => ({
            lineId: slot.lineId,
            accountId: form.slots[slot.lineId] ?? '',
        }))
        .filter((choice) => choice.accountId.length > 0);

/**
 * The accounts a slot's picker offers: postable, unarchived leaves whose code
 * starts with one of the slot's prefixes.
 *
 * An **empty filter means every postable account**, which is what the template
 * model says it means — not "none". Getting that backwards would render an empty
 * dropdown on a slot the accountant deliberately left open.
 *
 * The filter is a narrowing of the picker, and the server enforces it again at
 * record time (`SLOT_OUTSIDE_FILTER`). Applying it here is what stops the user
 * choosing something that will be refused; it is not what makes the refusal
 * safe.
 */
export const accountsForSlot = (
    accounts: readonly ChartAccount[],
    slot: RecordableSlot,
): ChartAccount[] => {
    const postable = accounts.filter(
        (account) => account.isPostable && !account.archived,
    );
    const matching =
        slot.accountFilter.length === 0
            ? postable
            : postable.filter((account) =>
                  slot.accountFilter.some((prefix) =>
                      account.code.startsWith(prefix),
                  ),
              );
    return matching.slice().sort((a, b) => a.code.localeCompare(b.code));
};

/** `1110 — Cash on hand`, the way every account picker in this folder reads. */
export const accountOptionLabel = (account: ChartAccount): string =>
    `${account.code} — ${account.name}`;

/** A slot's label in the reader's own language, falling back to the other. */
export const slotLabel = (slot: RecordableSlot, locale: LocaleCode): string =>
    (locale === 'ka' ? slot.labelKa : slot.labelEn) ||
    slot.labelEn ||
    slot.labelKa;

// ── what the date makes available ───────────────────────────────────────────

/**
 * The rows a user could actually choose.
 *
 * The picker's list carries three kinds of row — recordable, in force but not
 * fillable by this build, and outside its window — and almost every count on
 * this screen means the first kind only.
 */
export const recordableOperations = (
    items: readonly RecordableOperation[],
): RecordableOperation[] =>
    items.filter((item) => item.unrecordableReason === 'NONE');

/**
 * The single operation this session may record, or `null`.
 *
 * A picker offering one choice is a control that cannot be used wrongly and
 * cannot be used usefully either, so the screen resolves it and shows the form
 * instead. Deliberately **not** "the first recordable one": a list of six with
 * five out of their windows still has six operations, and auto-choosing there
 * would be picking on the user's behalf from a list they can see.
 */
export const soleOperation = (
    items: readonly RecordableOperation[],
): RecordableOperation | null => {
    const recordable = recordableOperations(items);
    return items.length === 1 && recordable.length === 1
        ? (recordable[0] as RecordableOperation)
        : null;
};

/**
 * What the chosen date leaves on the screen.
 *
 * The recordable list is resolved **by date** on the server, so an empty list is
 * ambiguous on its own: it means "you backdated past every recipe" or "your role
 * permits nothing", and those are different sentences with different
 * affordances. Every shape below is one of those readings, named, so the View
 * renders a branch instead of reasoning about counts.
 */
export type DateAvailability =
    /** At least one operation is in force. `configured` may exceed `available`. */
    | { kind: 'available'; available: number; configured: number }
    /**
     * Nothing is in force and the date sits **before** the earliest recipe ever
     * started — the one case where the date itself is what is wrong, so the
     * field goes to an error state and the jump has somewhere obvious to go.
     */
    | { kind: 'beforeEarliest'; earliest: string }
    /**
     * Nothing is in force on a date that is not before everything: a gap between
     * versions, or every window has since expired. The date is not obviously a
     * mistake, so the field is left alone.
     */
    | { kind: 'noneOnDate'; earliest: string | null }
    /**
     * This session could record nothing on any date. Not an error — a role may
     * permit nothing — and there is no date to jump to.
     */
    | { kind: 'nothingConfigured' };

/**
 * Read the picker's answer as one of the four states above.
 *
 * `earliestEffectiveFrom` is only trusted when it is a real ISO date. The server
 * sends one whenever `configuredCount > 0`, but a client that renders "the
 * earliest recipe starts null" because it assumed so is worse than one that
 * quietly drops the jump button.
 */
export const dateAvailability = (
    recordable: RecordableOperations,
): DateAvailability => {
    // What can actually be chosen, not what is listed. Since BOS-148 the list
    // also carries operations the date has taken away, so counting rows would
    // report a date that offers nothing as offering nine things.
    const available = recordableOperations(recordable.items).length;
    const configured = Math.max(recordable.configuredCount, available);

    if (available > 0) {
        return { kind: 'available', available, configured };
    }
    if (configured === 0) {
        return { kind: 'nothingConfigured' };
    }

    const earliest = recordable.earliestEffectiveFrom;
    if (earliest && recordable.date < earliest) {
        return { kind: 'beforeEarliest', earliest };
    }
    return { kind: 'noneOnDate', earliest };
};

/**
 * The date a "Jump to…" button would move to, or `null` when there is nowhere
 * to jump.
 *
 * `earliestEffectiveFrom` is the earliest date any recipe was in force, so at
 * least one operation is available there by construction — the jump always lands
 * on a non-empty list, which is the only thing that makes it worth offering.
 */
export const jumpTarget = (availability: DateAvailability): string | null =>
    availability.kind === 'beforeEarliest' || availability.kind === 'noneOnDate'
        ? availability.earliest
        : null;

/**
 * The status line under the date field.
 *
 * The two-count shape is the whole point of carrying `configuredCount`: it is
 * the only moment a user learns the date is hiding operations from them, so it
 * is derived from the numbers rather than assumed from a short list.
 */
export type DateStatus =
    | { kind: 'all'; available: number }
    | { kind: 'narrowed'; available: number; configured: number }
    | { kind: 'none' };

export const dateStatus = (availability: DateAvailability): DateStatus => {
    if (availability.kind !== 'available') {
        return { kind: 'none' };
    }
    return availability.configured > availability.available
        ? {
              kind: 'narrowed',
              available: availability.available,
              configured: availability.configured,
          }
        : { kind: 'all', available: availability.available };
};

/**
 * The empty state's two sentences, as i18n keys and their arguments.
 *
 * Resolved here rather than in the component so the branch is a value a test can
 * assert on, and so the View only ever calls `t`.
 */
export type NoOperationsCopy = {
    titleKey: string;
    bodyKey: string;
    /** `date` is already in the app's `DD/MM/YYYY` display form. */
    bodyArgs: { date: string };
};

export const noOperationsCopy = (
    availability: Exclude<DateAvailability, { kind: 'available' }>,
): NoOperationsCopy => {
    if (availability.kind === 'nothingConfigured') {
        return {
            titleKey: 'operations.record.noOperations.nothingTitle',
            bodyKey: 'operations.record.noOperations.nothingBody',
            bodyArgs: { date: '' },
        };
    }
    if (availability.kind === 'beforeEarliest') {
        return {
            titleKey: 'operations.record.noOperations.beforeTitle',
            bodyKey: 'operations.record.noOperations.beforeBody',
            bodyArgs: { date: isoToDisplayDate(availability.earliest) },
        };
    }
    return {
        titleKey: 'operations.record.noOperations.gapTitle',
        bodyKey: 'operations.record.noOperations.gapBody',
        bodyArgs: { date: '' },
    };
};

/**
 * The period warning to render, or `null`.
 *
 * **Only the negative half is rendered.** `OPEN` also covers a tenant that has
 * generated no fiscal year at all — for them nothing is open or closed, the
 * calendar is opt-in — so a cheerful "this month is open" would be describing
 * something that may not exist. Silence already reads as "nothing in the way",
 * and the post-time refusal remains the gate either way.
 */
export type PeriodNotice = 'closed' | 'notDefined';

export const periodNotice = (
    status: RecordingPeriodStatus,
): PeriodNotice | null => {
    if (status === 'CLOSED') {
        return 'closed';
    }
    return status === 'NOT_DEFINED' ? 'notDefined' : null;
};

/** ISO `YYYY-MM-DD` for the day before today, in the browser's own zone. */
export const yesterdayIso = (): string => {
    const date = new Date();
    date.setDate(date.getDate() - 1);
    return toIsoDate(date);
};

// ── the shape of the entry, before any amount ───────────────────────────────

/**
 * One row of the entry a recipe *would* post, drawn before an amount exists.
 *
 * **This is a shape, and it is not authoritative.** The numbers — and which
 * lines survive to be posted at all — come from `POST .../preview`, which is the
 * only thing that knows the VAT rate in force, the rounding, and that the engine
 * merges two lines resolving to the same account and drops a VAT line computing
 * nil. So a three-line recipe can post two, and this view can be one row long
 * where the entry is not.
 *
 * It exists because the panel beside an irreversible Post button should start
 * explaining the entry at the moment of the pick rather than after the last
 * field. The server's answer replaces it the instant one arrives, and the button
 * gates on that answer either way — never on this.
 */
export type EntryShapeRow = {
    /** The template line this came from. Unique within a recipe. */
    lineId: string;
    pairGroup: number;
    side: OperationLineSide;
    /** `1110 — Cash on hand`, or the slot's own question while unanswered. */
    label: string;
    /** True while this row is a slot nobody has answered yet. */
    isPending: boolean;
};

/**
 * The recipe's lines, pinned and slot alike, in the order they read.
 *
 * An answered slot renders as the account it resolved to — the same string the
 * dropdown shows — so filling the form visibly turns the shape into the entry
 * rather than replacing one list with another.
 */
export const entryShape = (
    operation: RecordableOperation | null,
    form: RecordForm,
    accounts: readonly ChartAccount[],
    locale: LocaleCode,
): EntryShapeRow[] => {
    if (!operation) {
        return [];
    }
    const byId = new Map(accounts.map((account) => [account.id, account]));

    const pinned: EntryShapeRow[] = operation.pinnedLines.map((line) => ({
        lineId: line.lineId,
        pairGroup: line.pairGroup,
        side: line.side,
        label: `${line.code} — ${line.name}`,
        isPending: false,
    }));

    const slots: EntryShapeRow[] = operation.slots.map((slot) => {
        const chosen = byId.get(form.slots[slot.lineId] ?? '');
        return {
            lineId: slot.lineId,
            pairGroup: slot.pairGroup,
            side: slot.side,
            label: chosen
                ? accountOptionLabel(chosen)
                : slotLabel(slot, locale),
            isPending: !chosen,
        };
    });

    return [...pinned, ...slots].sort(
        (a, b) =>
            a.pairGroup - b.pairGroup ||
            (a.side === 'DEBIT' ? 0 : 1) - (b.side === 'DEBIT' ? 0 : 1),
    );
};

/**
 * The accounting period the entry lands in, as a month and year.
 *
 * Derived from the entry date rather than from the period calendar: the calendar
 * is opt-in, and the sentence is about where the entry books, which is true
 * whether or not a fiscal year exists. Whether that period will *accept* a post
 * is the date band's question and it is answered there.
 */
export const postingPeriodLabel = (
    isoDate: string,
    language: string | undefined,
): string => formatInLanguage(isoDate, 'MMMM YYYY', language);

// ── server refusals ─────────────────────────────────────────────────────────

/**
 * Where a coded refusal belongs on the screen.
 *
 * `slot` is not a target on its own: which dropdown a slot error belongs to
 * depends on *which* slot, and the server names it in prose rather than in a
 * field. See {@link toRecordError}.
 */
export type RecordErrorTarget =
    'date' | 'amount' | 'slots' | 'operation' | 'form';

const TARGETS: Record<string, RecordErrorTarget> = {
    // The date is the one field that decides all three of these: which version
    // applies, and whether the period will take a posting at all.
    PERIOD_CLOSED: 'date',
    PERIOD_NOT_DEFINED: 'date',
    NO_ACTIVE_VERSION: 'date',
    AMOUNT_NOT_POSITIVE: 'amount',
    SLOT_MISSING: 'slots',
    SLOT_OUTSIDE_FILTER: 'slots',
    SLOT_UNKNOWN: 'slots',
    PAIR_SAME_ACCOUNT: 'slots',
    ACCOUNT_NOT_FOUND: 'slots',
    ACCOUNT_ARCHIVED: 'slots',
    ACCOUNT_NOT_POSTABLE: 'slots',
    // Not about anything the user typed: the operation itself is unavailable to
    // them, or unavailable at all. Anchoring these to a field would invite an
    // edit that cannot help.
    ROLE_NOT_ALLOWED: 'operation',
    TEMPLATE_BLOCKED: 'operation',
    TEMPLATE_NOT_FOUND: 'operation',
    SECOND_AMOUNT_REQUIRED: 'operation',
};

export type RecordError = {
    target: RecordErrorTarget;
    /** The server's own message. See below for why it is not translated. */
    message: string;
    code: string | null;
};

/**
 * Turn a failed preview or record into something to render.
 *
 * **The server's prose is shown verbatim**, the same trade the editor and the
 * periods screen document. These messages name the thing that was wrong —
 * "Account 5100 is not one of the accounts 'Paid from' accepts (11, 12…)",
 * "accounting period 6 of fiscal year 2026 is CLOSED" — and a translated string
 * could not carry the account, the prefixes or the period without the server
 * sending each separately. Anchoring the message to the right part of the form
 * is what `target` is for, and it is the half that matters more.
 *
 * The known consequence: **a Georgian user sees English here.** Tolerable for
 * the same reason as the editor's — the form gates on its own rules first (every
 * slot answered, an amount that parses), so these are the refusals that got past
 * it, not the everyday ones.
 */
export const toRecordError = (
    error: ErrorResponse | null | undefined,
): RecordError | null => {
    if (!error) {
        return null;
    }
    const code = error.code ?? null;
    return {
        target: (code && TARGETS[code]) || 'form',
        message: error.message,
        code,
    };
};
